/*
Copyright 2026 Rappler Inc.

SPDX-License-Identifier: AGPL-3.0-only OR GPL-3.0-only
Please see LICENSE files in the repository root for full details.
*/

import SdkConfig from "../SdkConfig";
import { getConfiguredMobileAppUrls } from "./mobileBuilds";

/**
 * Phone user agents. Tablets are deliberately absent: the Rappler application is a phone
 * application, and an iPad reports itself as a desktop Safari on current iPadOS anyway, so any rule
 * claiming to cover tablets would be wrong about half of them.
 */
const PHONE_USER_AGENT = /Android.*Mobile|iPhone|iPod/i;

export const MOBILE_APP_LANDING_PATH = "/mobile";
export const LEGACY_MOBILE_GUIDE_PATH = "/mobile_guide";

const pathMatches = (pathname: string, path: string): boolean => pathname === path || pathname === `${path}/`;

export const isMobileAppLandingPath = (pathname: string = window.location.pathname): boolean =>
    pathMatches(pathname, MOBILE_APP_LANDING_PATH);

export const isLegacyMobileGuidePath = (pathname: string = window.location.pathname): boolean =>
    pathMatches(pathname, LEGACY_MOBILE_GUIDE_PATH) || pathname.startsWith(`${LEGACY_MOBILE_GUIDE_PATH}/`);

export const getMobileLandingDestination = (location: Pick<Location, "pathname" | "search" | "hash">): string =>
    `${location.pathname}${location.search}${location.hash}`;

export const buildMobileAppLandingUrl = (
    location: Pick<Location, "origin" | "pathname" | "search" | "hash"> = window.location,
): string => {
    const url = new URL(MOBILE_APP_LANDING_PATH, location.origin);
    url.searchParams.set("destination", getMobileLandingDestination(location));
    return url.toString();
};

export const getSavedMobileDestination = (search: string = window.location.search): string => {
    const destination = new URLSearchParams(search).get("destination");
    return destination?.startsWith("/") && !destination.startsWith("//") ? destination : "/";
};

/**
 * Build the custom-scheme handoff accepted by the Rappler app. A room destination keeps its room
 * alias or ID. Other mobile entry points still open the application at its normal starting screen.
 */
export const buildMobileAppDeepLink = (destination: string): string => {
    try {
        const fragment = new URL(destination, "https://chat.muni.rappler.com").hash.replace(/^#\/?/, "");
        const [fragmentPath] = fragment.split("?", 1);
        const [screen, room] = fragmentPath.split("/");

        if (screen !== "room" || !room) return "muni://";
        return `muni://room/${encodeURIComponent(decodeURIComponent(room))}`;
    } catch {
        return "muni://";
    }
};

/**
 * Whether this browser can be sent somewhere useful. A gate that stops a visitor without offering a
 * store for their own platform is a dead end, so each platform is checked against its own links
 * rather than against whether any link exists at all.
 */
const hasStoreForThisPlatform = (userAgent: string): boolean => {
    const urls = getConfiguredMobileAppUrls();
    if (/iPhone|iPod/i.test(userAgent)) return Boolean(urls.ios);
    if (/Android/i.test(userAgent)) return Boolean(urls.android || urls.fdroid);
    return false;
};

/**
 * Muni: whether to hold this browser at the app download screen instead of letting it reach the
 * client.
 *
 * Called after the configuration is loaded, because every input comes from it.
 */
export const shouldGateMobileBrowser = (userAgent: string = navigator.userAgent): boolean => {
    if (SdkConfig.get().mobile_app_gate === false) return false;
    if (!PHONE_USER_AGENT.test(userAgent)) return false;
    return hasStoreForThisPlatform(userAgent);
};
